import { useState, useRef } from 'react'
import { Settings2, Lock, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  usePlaygroundMcpServers,
  useTogglePlaygroundMcpServer,
  type PlaygroundMcpServer,
} from '@/lib/api/hooks'
import { cn } from '@/lib/utils'

export function McpServerToggle() {
  const [open, setOpen] = useState(false)
  const { data: servers, isLoading } = usePlaygroundMcpServers()
  const toggleMutation = useTogglePlaygroundMcpServer()
  const [togglingUuid, setTogglingUuid] = useState<string | null>(null)
  const pendingRef = useRef(false)

  const enabledCount = servers?.filter((s) => s.is_enabled && !s.is_default).length ?? 0

  const handleToggle = async (server: PlaygroundMcpServer) => {
    if (!server.can_toggle || pendingRef.current) return
    pendingRef.current = true
    setTogglingUuid(server.uuid)
    toggleMutation.mutate(
      { serverUuid: server.uuid, enabled: !server.is_enabled },
      {
        onSettled: () => {
          pendingRef.current = false
          setTogglingUuid(null)
        },
      },
    )
  }

  if (!isLoading && (!servers || servers.length === 0)) {
    return null
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'h-10 px-2.5 shrink-0 text-xs text-muted-foreground hover:text-foreground gap-1',
                enabledCount > 0 && 'text-primary',
              )}
            >
              <Settings2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">MCP</span>
              {enabledCount > 0 && (
                <Badge variant="secondary" className="h-4 px-1 text-[10px] leading-none">
                  +{enabledCount}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-xs">Toggle MCP servers</p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent className="w-fit min-w-48 max-w-80 p-2" align="end">
        {isLoading ? (
          <div className="flex items-center justify-center py-3">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-1">
            {servers?.map((server) => (
              <ServerRow
                key={server.uuid}
                server={server}
                onToggle={handleToggle}
                togglingUuid={togglingUuid}
              />
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface ServerRowProps {
  server: PlaygroundMcpServer
  onToggle: (server: PlaygroundMcpServer) => void
  togglingUuid: string | null
}

function ServerRow({ server, onToggle, togglingUuid }: ServerRowProps) {
  const isLocked = !server.can_toggle
  const isDefault = server.is_default
  const isThisToggling = togglingUuid === server.uuid

  return (
    <div
      className={cn(
        'flex items-center justify-between px-2 py-1.5 rounded-md text-sm',
        isLocked && 'opacity-50',
        server.is_enabled && !isLocked && 'bg-primary/5',
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0 flex-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="whitespace-nowrap cursor-default">{server.name}</span>
          </TooltipTrigger>
          {server.description && (
            <TooltipContent side="left" className="max-w-xs">
              <p className="text-xs">{server.description}</p>
            </TooltipContent>
          )}
        </Tooltip>
        {isDefault && (
          <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">
            Default
          </Badge>
        )}
        {isLocked && (
          <Tooltip>
            <TooltipTrigger>
              <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">Requires {server.required_feature}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <Switch
        checked={server.is_enabled}
        onCheckedChange={() => onToggle(server)}
        disabled={isLocked || isDefault || isThisToggling}
        className="data-[state=checked]:bg-primary scale-75 shrink-0"
      />
    </div>
  )
}
